import { type CourseRef, useClasses, useCourses } from '@shkola/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { Select } from './ui';

function useAvailableCourses() {
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId);
  const [adminClass, setAdminClass] = useState<string>();
  const classId =
    ctx.kind === 'teacher'
      ? ctx.homeroomClass?.id
      : ctx.kind === 'admin'
        ? (adminClass ?? classes.data?.[0]?.id)
        : undefined;
  const mine = useCourses({ teacherMemberId: ctx.kind === 'teacher' ? ctx.memberId : '' });
  const ofClass = useCourses({ classId: classId ?? '' });
  const groups: Array<{ label: string; courses: CourseRef[] }> = [];
  if (ctx.kind === 'teacher') groups.push({ label: 'mine', courses: mine.data ?? [] });
  if (classId) {
    const own = new Set((mine.data ?? []).map((c) => c.id));
    groups.push({ label: 'class', courses: (ofClass.data ?? []).filter((c) => !own.has(c.id)) });
  }
  return {
    groups,
    loading: mine.isPending && ctx.kind === 'teacher',
    classes: classes.data ?? [],
    adminClass: classId,
    setAdminClass,
  };
}

const courseLabel = (c: CourseRef) =>
  `${c.classLabel} · ${c.subject}${c.groupLabel ? ` (${c.groupLabel})` : ''}`;

/**
 * «Class · subject» choice for teachers (their courses, plus their homeroom class) and the
 * administration (a class first, then its courses). Shared by the journal and homework.
 */
export function useCoursePicker() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const available = useAvailableCourses();
  const [courseId, setCourseId] = useState<string>();
  const all = available.groups.flatMap((g) => g.courses);
  const course = all.find((c) => c.id === courseId) ?? all[0];

  const controls = (
    <>
      {ctx.kind === 'admin' && (
        <Select
          aria-label={t('schedule.class')}
          className="!h-11 !rounded-full !pr-3 !pl-4"
          value={available.adminClass ?? ''}
          onChange={(e) => available.setAdminClass(e.target.value)}
        >
          {available.classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </Select>
      )}
      <Select
        aria-label={t('journal.course')}
        className="!h-11 max-w-full !rounded-full !pr-3 !pl-4"
        value={course?.id ?? ''}
        onChange={(e) => setCourseId(e.target.value)}
      >
        {available.groups.map((g) =>
          g.courses.length === 0 ? null : (
            <optgroup
              key={g.label}
              label={
                g.label === 'mine'
                  ? t('journal.myCourses')
                  : t('journal.classCourses', { class: g.courses[0]?.classLabel ?? '' })
              }
            >
              {g.courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {courseLabel(c)}
                </option>
              ))}
            </optgroup>
          ),
        )}
      </Select>
    </>
  );
  return { course, loading: available.loading, controls };
}
